import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  ArrowLeft, Loader2, TrendingUp, TrendingDown, Activity, DollarSign, Target,
  Eye, Users, Coins, Zap, ShieldCheck, RefreshCw, Wallet, Plus, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StakeholderEventDashboard() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [showBuy, setShowBuy] = useState(false);
  const [showTopup, setShowTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState(500);
  const [qty, setQty] = useState(1);

  // What-if simulator: per-tier ticket simulator. For each ticket tier:
  // simTickets[ticket_type_id] = simulated total tickets sold (replaces actuals)
  const [simTickets, setSimTickets] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([
        axios.get(`${API}/stakeholders/events/${eventId}/dashboard`, { headers }),
        axios.get(`${API}/stakeholders/me`, { headers }),
      ]);
      setData(d.data);
      setMe(m.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtBig = (cents) => `$${Math.round((cents || 0) / 100).toLocaleString()}`;

  const buy = async () => {
    setBuying(true);
    try {
      const r = await axios.post(`${API}/stakeholders/offerings/${eventId}/buy`, { qty }, { headers });
      toast.success(`Bought ${r.data.purchased} NFT Ticket${r.data.purchased > 1 ? 's' : ''} at market rate`);
      setShowBuy(false);
      setQty(1);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Buy failed';
      if (detail.toLowerCase().includes('insufficient')) {
        toast.error('Not enough wallet balance — top up first');
        setShowBuy(false);
        setShowTopup(true);
      } else {
        toast.error(detail);
      }
    }
    setBuying(false);
  };

  const startTopup = async () => {
    try {
      const r = await axios.post(`${API}/stakeholders/wallet/topup`, { amount_cents: Math.round(topupAmount * 100) }, { headers });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Top-up failed'); }
  };

  // Initialize simulator with current actuals when data loads
  useEffect(() => {
    if (data?.ticket_tiers && Object.keys(simTickets).length === 0) {
      const init = {};
      data.ticket_tiers.forEach((t) => { init[t.ticket_type_id] = t.quantity_sold || 0; });
      setSimTickets(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Simulator math — per-tier ticket counts → revenue → profit → pool → my payout
  const simulation = useMemo(() => {
    if (!data) return null;
    const off = data.offering || {};
    const summary = data.summary || {};
    const projected = data.projected || {};
    const tiers = data.ticket_tiers || [];
    const reservePct = projected.eventism_reserve_pct || 0.30;
    const maxSupply = Math.max(1, off.max_supply || 1);

    // Compute simulated ticket revenue from sliders (in cents)
    let simTicketRevenueCents = 0;
    let simTicketsSold = 0;
    tiers.forEach((t) => {
      const qty = Math.max(0, simTickets[t.ticket_type_id] ?? t.quantity_sold ?? 0);
      simTicketRevenueCents += Math.round((t.price || 0) * 100) * qty;
      simTicketsSold += qty;
    });

    // Replace actual ticket revenue with simulated, keep other actuals + estimates
    const baseRevenue = Math.max(summary.actual_income_cents || 0, summary.estimated_income_cents || 0);
    const actualTicketRev = summary.ticket_revenue_cents || 0;
    const projIncome = baseRevenue - actualTicketRev + simTicketRevenueCents;

    // Expenses: use the higher of actual or estimated
    const projExpense = Math.max(summary.actual_expense_cents || 0, summary.estimated_expense_cents || 0);
    const profit = projIncome - projExpense;
    const profitFloored = Math.max(0, profit);
    const reserve = Math.round(profitFloored * reservePct);
    const fullPool = profitFloored - reserve;
    const perShare = Math.floor(fullPool / maxSupply);
    const myShares = data.my_position?.shares || 0;
    const myPayout = perShare * myShares;
    const myCost = data.my_position?.cost_cents || 0;
    const myPnl = myPayout - myCost;
    const breakeven = profit >= 0;

    return {
      simTicketsSold, simTicketRevenueCents,
      profit, profitFloored, reserve, fullPool, perShare,
      myShares, myPayout, myCost, myPnl, breakeven,
      maxSupply, reservePct,
    };
  }, [data, simTickets]);

  if (loading) return <div className="min-h-screen bg-background pt-20 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-400" /></div>;
  if (!data) return null;

  const { event, offering, current_price_cents, shares_remaining, summary, page_views, projected, my_position, cap_table } = data;
  const wallet = me?.wallet || {};
  const balance = wallet.balance_cents || 0;
  const canAfford = balance >= current_price_cents;
  const breakevenReached = (summary?.breakeven_progress_pct || 0) >= 100;
  const reservePct = projected?.eventism_reserve_pct || 0.30;

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="trading-dashboard">
      {/* Sticky wallet bar */}
      <div className="sticky top-14 z-40 bg-slate-950/95 backdrop-blur border-b border-border" data-testid="sticky-wallet-bar">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Wallet className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Wallet</span>
            <span className="text-lg font-bold text-emerald-400 font-mono" data-testid="wallet-balance">{fmt(balance)}</span>
            {!canAfford && offering?.status === 'open' && (
              <Badge variant="outline" className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">below current price</Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowTopup(true)} data-testid="open-topup-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add funds
            </Button>
            {offering?.status === 'open' && shares_remaining > 0 && (
              <Button size="sm" onClick={() => setShowBuy(true)} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="open-buy-dialog-sticky">
                Buy NFT — {fmt(current_price_cents)}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pt-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/stakeholder/dashboard')} className="mb-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>

        {/* Hero */}
        <div className="rounded-xl overflow-hidden border border-border mb-4 relative">
          {event?.image_url && <img src={event.image_url} alt={event.title} className="w-full h-44 object-cover opacity-60" />}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          <div className="absolute bottom-0 p-5 w-full">
            <div className="flex justify-between items-end flex-wrap gap-3">
              <div>
                <Badge variant="outline" className="mb-2">{offering?.status?.toUpperCase()}</Badge>
                <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']" data-testid="event-title">{event?.title || event?.name || 'Untitled event'}</h1>
                <p className="text-xs text-muted-foreground mt-1">
                  {[event?.venue?.name, event?.city].filter(Boolean).join(' · ') || ''}
                </p>
              </div>
              {breakevenReached && (
                <div className="bg-emerald-500/20 border border-emerald-500/40 rounded-lg px-3 py-1.5 flex items-center gap-2 text-xs font-semibold text-emerald-300">
                  <Zap className="w-3.5 h-3.5" /> BREAK-EVEN REACHED
                </div>
              )}
            </div>
          </div>
        </div>

        {/* HERO: huge projected payout + reserve split */}
        <Card className="mb-4 border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-950/30 via-indigo-950/20 to-slate-950/0" data-testid="big-payout-card">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-fuchsia-300 mb-2">
              <Target className="w-3.5 h-3.5" /> Your projected payout
            </div>
            <div className="flex items-end gap-4 flex-wrap">
              <div className="font-['Space_Grotesk'] text-5xl sm:text-7xl font-bold font-mono leading-none text-emerald-400" data-testid="big-projected-payout">
                {fmtBig(my_position?.projected_payout_cents)}
              </div>
              <div className="pb-2 text-sm">
                <div className="text-muted-foreground">on {my_position?.shares || 0} NFT Ticket{my_position?.shares === 1 ? '' : 's'}</div>
                <div className={`flex items-center gap-1 font-semibold ${(my_position?.projected_pnl_cents || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {(my_position?.projected_pnl_cents || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  P&amp;L {fmt(my_position?.projected_pnl_cents)}
                </div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Mini label={`Per-NFT payout (1 of ${offering?.max_supply})`} value={fmt(projected?.payout_per_share_cents)} />
              <Mini label={`Holder pool (${Math.round((1 - reservePct) * 100)}%)`} value={fmt(projected?.full_pool_for_holders_cents)} />
              <Mini label={`Distributed to date (${projected?.subscription_pct || 0}% subscribed)`} value={fmt(projected?.pool_for_holders_cents)} />
              <Mini label={`Eventism reserve (${Math.round(reservePct * 100)}% + unsubscribed)`} value={fmt((projected?.eventism_reserve_cents || 0) + (projected?.eventism_unsubscribed_cents || 0))} />
            </div>
            <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
              Each NFT Ticket = 1 / {offering?.max_supply} of the holder pool. Un-subscribed NFT Tickets stay with Eventism.
            </p>
          </CardContent>
        </Card>

        {/* Top stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card data-testid="stat-current-price"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Coins className="w-3 h-3" /> Market price</div><div className="text-2xl font-bold mt-1 text-fuchsia-300 font-mono">{fmt(current_price_cents)}</div><div className="text-[10px] text-muted-foreground mt-1">{shares_remaining} of {offering?.max_supply} NFTs left</div></CardContent></Card>
          <Card data-testid="stat-cac"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Users className="w-3 h-3" /> CAC</div><div className="text-2xl font-bold mt-1 font-mono">{fmt(summary?.cac_cents)}</div><div className="text-[10px] text-muted-foreground mt-1">{summary?.unique_customers || 0} unique customers</div></CardContent></Card>
          <Card data-testid="stat-page-views"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Eye className="w-3 h-3" /> Page views (24h)</div><div className="text-2xl font-bold mt-1 font-mono">{(page_views?.last_24h || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground mt-1">{(page_views?.total || 0).toLocaleString()} all-time</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Reserve split</div><div className="text-2xl font-bold mt-1 font-mono">{Math.round((1-reservePct)*100)}/{Math.round(reservePct*100)}</div><div className="text-[10px] text-muted-foreground mt-1">holders / Eventism</div></CardContent></Card>
        </div>

        {/* Pro forma */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> Live Pro Forma</h3>
              <div className="space-y-2 text-sm">
                <Row label="Tickets sold" value={summary?.tickets_sold || 0} />
                <Row label="Ticket revenue" value={fmt(summary?.ticket_revenue_cents)} />
                <Row label="Sponsorship revenue" value={fmt(summary?.sponsorship_revenue_cents)} />
                <hr className="border-border" />
                <Row label="Actual income" value={fmt(summary?.actual_income_cents)} bold />
                <Row label="Projected income" value={fmt(summary?.estimated_income_cents)} muted />
                <hr className="border-border" />
                <Row label="Actual expense" value={fmt(summary?.actual_expense_cents)} />
                <Row label="Projected expense" value={fmt(summary?.estimated_expense_cents)} muted />
                <hr className="border-border" />
                <Row label="Marketing spend" value={fmt(summary?.marketing_spend_cents)} />
                <Row label="Forecast NET PROFIT" value={fmt(projected?.profit_cents)} bold positive />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-indigo-400" /> Breakeven progress</h3>
              <div className="text-sm text-muted-foreground mb-1">{summary?.breakeven_progress_pct}% to target</div>
              <div className="w-full bg-slate-800 rounded-full h-3 mb-3"><div className={`h-full rounded-full transition-all ${breakevenReached ? 'bg-emerald-500' : 'bg-gradient-to-r from-indigo-500 to-fuchsia-500'}`} style={{ width: `${Math.min(100, summary?.breakeven_progress_pct || 0)}%` }} /></div>
              <div className="text-xs text-muted-foreground space-y-1">
                <Row label="Target" value={fmt(summary?.breakeven_target_cents)} />
                <Row label="Gap remaining" value={fmt(summary?.breakeven_gap_cents)} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* What-if simulator (per ticket tier) — placed right above the cap table */}
        <Card className="mb-4 border-indigo-500/30" data-testid="simulator-card">
          <CardContent className="p-5">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-400" /> What-if simulator
                <span className="text-xs font-normal text-muted-foreground">— move the sliders to see your payout</span>
              </h3>
              <Button size="sm" variant="ghost" onClick={() => {
                const reset = {};
                (data.ticket_tiers || []).forEach((t) => { reset[t.ticket_type_id] = t.quantity_sold || 0; });
                setSimTickets(reset);
              }} data-testid="sim-reset-btn">Reset to actuals</Button>
            </div>
            {(!data.ticket_tiers || data.ticket_tiers.length === 0) ? (
              <p className="text-sm text-muted-foreground text-center py-3">No ticket tiers set up for this event yet — admin needs to create ticket types in the Event Manager.</p>
            ) : (
              <>
                <div className="space-y-4">
                  {data.ticket_tiers.map((t) => {
                    const sold = simTickets[t.ticket_type_id] ?? t.quantity_sold ?? 0;
                    const cap = t.quantity_total || 100;
                    const tierRevenue = (t.price || 0) * sold;
                    return (
                      <div key={t.ticket_type_id} data-testid={`sim-tier-${t.ticket_type_id}`}>
                        <div className="flex justify-between text-sm mb-1.5 flex-wrap gap-2">
                          <span><strong className="text-foreground">{t.name}</strong> <span className="text-muted-foreground">${(t.price || 0).toFixed(0)} ea — currently {t.quantity_sold || 0} sold of {cap}</span></span>
                          <span className="font-mono"><strong className="text-fuchsia-300">{sold}</strong> tickets · <strong className="text-emerald-400">${tierRevenue.toLocaleString()}</strong></span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Slider value={[sold]} onValueChange={(v) => setSimTickets({ ...simTickets, [t.ticket_type_id]: v[0] })} min={0} max={cap} step={1} className="flex-1" data-testid={`sim-tier-slider-${t.ticket_type_id}`} />
                          <Input type="number" min="0" max={cap} value={sold} onChange={(e) => setSimTickets({ ...simTickets, [t.ticket_type_id]: Math.max(0, Math.min(cap, parseInt(e.target.value) || 0)) })} className="w-20 text-sm font-mono" />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sim-results">
                  <SimStat label="Tickets sold" value={(simulation?.simTicketsSold || 0).toLocaleString()} />
                  <SimStat label="Ticket revenue" value={fmt(simulation?.simTicketRevenueCents)} />
                  <SimStat label={simulation?.breakeven ? 'Forecast profit' : 'Forecast LOSS'} value={fmt(simulation?.profit)} accent={simulation?.breakeven} hero={!simulation?.breakeven} />
                  <SimStat label="YOUR PAYOUT" value={fmtBig(simulation?.myPayout)} hero />
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <Mini label={`Per-NFT (1 of ${simulation?.maxSupply})`} value={fmt(simulation?.perShare)} />
                  <Mini label="Holder pool (70%)" value={fmt(simulation?.fullPool)} />
                  <Mini label={`P&L on ${simulation?.myShares} NFT${simulation?.myShares === 1 ? '' : 's'}`} value={fmt(simulation?.myPnl)} />
                </div>
                {!simulation?.breakeven && (
                  <div className="mt-3 rounded-md bg-rose-500/10 border border-rose-500/30 p-3 text-xs text-rose-200" data-testid="sim-loss-warning">
                    ⚠️ At this ticket count the event runs at a loss — your NFT Tickets would receive $0 payout (event must be net-positive for any holder distribution).
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Cap table — Fair Pool Indicator */}
        <Card className="mb-6" data-testid="cap-table-card">
          <CardContent className="p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-cyan-400" /> Cap table — {offering?.shares_sold || 0} of {offering?.max_supply} NFT Tickets sold ({Math.round((offering?.shares_sold || 0) / Math.max(1, offering?.max_supply || 1) * 100)}% subscribed)</h3>
            {(!cap_table || cap_table.length === 0) ? (
              <div className="text-sm text-muted-foreground text-center py-4">No NFT Tickets sold yet.</div>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm" data-testid="fair-pool-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="text-left px-2 py-2">Holder</th>
                      <th className="text-right px-2 py-2">NFT Tickets</th>
                      <th className="text-right px-2 py-2">Paid</th>
                      <th className="text-right px-2 py-2">Pool slice</th>
                      <th className="text-right px-2 py-2">Entitled to</th>
                      <th className="text-right px-2 py-2">P&amp;L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cap_table.slice(0, 100).map((c, i) => (
                      <tr key={i} className="border-b border-border/40 hover:bg-slate-900/40">
                        <td className="px-2 py-2 font-mono text-xs text-muted-foreground">{c.buyer}</td>
                        <td className="px-2 py-2 text-right font-mono"><strong>{c.nft_count}</strong></td>
                        <td className="px-2 py-2 text-right font-mono">{fmt(c.price_paid_cents)}</td>
                        <td className="px-2 py-2 text-right font-mono text-fuchsia-300 text-xs">{c.slice_label} <span className="text-muted-foreground">({c.slice_pct}%)</span></td>
                        <td className="px-2 py-2 text-right font-mono text-emerald-400">{fmt(c.entitled_cents)}</td>
                        <td className={`px-2 py-2 text-right font-mono ${c.pnl_cents >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{c.pnl_cents >= 0 ? '+' : ''}{fmt(c.pnl_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
              "Entitled to" = each holder's NFT count ÷ {offering?.max_supply} × the projected holder pool ({fmt(projected?.full_pool_for_holders_cents)}). Math is fully transparent — every backer can verify their slice.
            </p>
          </CardContent>
        </Card>

        <Button variant="ghost" size="sm" onClick={load} className="text-muted-foreground"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh now (auto every 15s)</Button>

        {/* BUY DIALOG */}
        <Dialog open={showBuy} onOpenChange={setShowBuy}>
          <DialogContent data-testid="buy-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Coins className="w-5 h-5 text-fuchsia-400" /> Purchase at MARKET RATE</DialogTitle>
              <DialogDescription>The price is set by Eventism's dynamic formula and locks in at the moment of purchase. Wallet will be debited immediately.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Quantity (max {shares_remaining})</label>
                <Input type="number" min="1" max={shares_remaining} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(shares_remaining, parseInt(e.target.value) || 1)))} data-testid="buy-qty-input" />
              </div>
              <div className="rounded-md bg-slate-900/50 border border-border p-3 text-sm space-y-1">
                <Row label="Market price per NFT Ticket" value={fmt(current_price_cents)} />
                <Row label="Quantity" value={qty} />
                <Row label="Estimated total" value={fmt(current_price_cents * qty)} bold />
                <Row label="Wallet balance after" value={fmt(balance - current_price_cents * qty)} muted />
              </div>
              <p className="text-[11px] text-muted-foreground">Note: prices update NFT-by-NFT as the formula recomputes (P = base + sold/supply × hype). Final total can shift slightly if other backers buy ahead of you.</p>
              {balance < current_price_cents * qty && (
                <div className="rounded-md bg-amber-500/10 border border-amber-500/40 p-3 text-sm flex items-center justify-between gap-2">
                  <span>Wallet short by {fmt(current_price_cents * qty - balance)}</span>
                  <Button size="sm" variant="outline" onClick={() => { setShowBuy(false); setShowTopup(true); }} data-testid="buy-shortfall-topup">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add funds
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBuy(false)}>Cancel</Button>
              <Button onClick={buy} disabled={buying || balance < current_price_cents * qty} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="confirm-buy-btn">
                {buying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />} Confirm at market rate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* TOPUP DIALOG */}
        <Dialog open={showTopup} onOpenChange={setShowTopup}>
          <DialogContent data-testid="topup-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-400" /> Add funds to your wallet</DialogTitle>
              <DialogDescription>Stripe processing fee is added on top so your wallet credit equals the exact amount you choose.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Amount (USD, min $50)</label>
                <Input type="number" min="50" step="50" value={topupAmount} onChange={(e) => setTopupAmount(Math.max(50, parseInt(e.target.value) || 0))} data-testid="topup-amount-input" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {[500, 1000, 2500, 5000, 10000].map((v) => (
                  <Button key={v} size="sm" variant="outline" onClick={() => setTopupAmount(v)}>${v.toLocaleString()}</Button>
                ))}
              </div>
              <div className="rounded-md bg-slate-900/50 border border-border p-3 text-sm space-y-1">
                <Row label="Wallet credit" value={fmt(topupAmount * 100)} bold />
                <Row label="Stripe fee" value={fmt(Math.round((topupAmount * 100 + 30) / 0.971) - topupAmount * 100)} muted />
                <Row label="You pay today" value={fmt(Math.round((topupAmount * 100 + 30) / 0.971))} bold />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTopup(false)}>Cancel</Button>
              <Button onClick={startTopup} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-topup-btn">
                <DollarSign className="w-4 h-4 mr-2" /> Continue to Stripe
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function Row({ label, value, bold, muted, positive }) {
  return (
    <div className={`flex justify-between ${muted ? 'text-muted-foreground' : ''}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={`${bold ? 'font-bold' : ''} ${positive ? 'text-emerald-400' : ''} font-mono`}>{value}</span>
    </div>
  );
}
function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-muted-foreground tracking-wider">{label}</div>
      <div className="text-base font-bold font-mono mt-0.5">{value}</div>
    </div>
  );
}
function SimStat({ label, value, accent, hero }) {
  return (
    <div className={`rounded-md p-3 border ${hero ? 'border-emerald-500/40 bg-emerald-500/5' : accent ? 'border-fuchsia-500/30 bg-fuchsia-500/5' : 'border-border'}`}>
      <div className="text-[10px] uppercase text-muted-foreground tracking-wider">{label}</div>
      <div className={`font-bold font-mono mt-1 ${hero ? 'text-2xl text-emerald-400' : 'text-base'}`}>{value}</div>
    </div>
  );
}
